import { useState, useEffect } from "react";
import axiosInstance from "../../redux/helpers/axios";
import Modal from "./Modal";
import CreateCampaignSegmentForm from "./CreateCampaignSegmentForm";

const ACCENT_FROM = "#E11D74";
const ACCENT_TO = "#EF4444";

export default function SegmentsPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [campaignId, setCampaignId] = useState("");
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState({ campaigns: true, segments: false });
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const fetchCampaigns = async () => {
      try {
        const res = await axiosInstance.get("/admin/campaign/get-all");
        const list = res.data.campaigns || [];
        setCampaigns(list);
        if (list.length > 0) setCampaignId(list[0].id); // default to the first campaign
      } catch (err) {
        setError(err?.response?.data?.error || "Failed to load campaigns");
      } finally {
        setLoading((prev) => ({ ...prev, campaigns: false }));
      }
    };
    fetchCampaigns();
  }, []);

  const fetchSegments = async (id) => {
    setLoading((prev) => ({ ...prev, segments: true }));
    try {
      const res = await axiosInstance.get(
        `/admin/campaign/segment/get-by-campaign/${id}`,
      );
      setSegments(res.data.segments || []);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to load segments");
    } finally {
      setLoading((prev) => ({ ...prev, segments: false }));
    }
  };

  useEffect(() => {
    if (campaignId) fetchSegments(campaignId);
  }, [campaignId]);

  const handleCreated = (segment) => {
    setSegments((prev) => [segment, ...prev]);
    setModalOpen(false);
  };

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Segments</h1>
          <p className="mt-1 text-sm text-gray-500">
            Segments within a campaign.
          </p>
        </div>
        <button
          onClick={() => setModalOpen(true)}
          disabled={!campaignId}
          className="rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          style={{
            background: `linear-gradient(90deg, ${ACCENT_FROM}, ${ACCENT_TO})`,
          }}
        >
          + New segment
        </button>
      </div>

      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700">
          Campaign
        </label>
        <select
          value={campaignId}
          onChange={(e) => setCampaignId(e.target.value)}
          disabled={loading.campaigns}
          className="mt-1 w-full max-w-sm rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2"
          style={{ "--tw-ring-color": ACCENT_FROM }}
        >
          {loading.campaigns && <option>Loading campaigns...</option>}
          {!loading.campaigns && campaigns.length === 0 && (
            <option>No campaigns yet</option>
          )}
          {campaigns.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-6 overflow-hidden rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Slug
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Description
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {loading.segments && (
              <tr>
                <td
                  colSpan={3}
                  className="px-4 py-6 text-center text-sm text-gray-400"
                >
                  Loading...
                </td>
              </tr>
            )}
            {!loading.segments && error && (
              <tr>
                <td
                  colSpan={3}
                  className="px-4 py-6 text-center text-sm text-red-600"
                >
                  {error}
                </td>
              </tr>
            )}
            {!loading.segments && !error && segments.length === 0 && (
              <tr>
                <td
                  colSpan={3}
                  className="px-4 py-6 text-center text-sm text-gray-400"
                >
                  No segments yet.
                </td>
              </tr>
            )}
            {!loading.segments &&
              !error &&
              segments.map((s) => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                    {s.name}
                  </td>
                  <td className="px-4 py-3 text-sm font-mono text-gray-500">
                    {s.slug}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {s.description || "—"}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title="New segment"
      >
        <CreateCampaignSegmentForm
          campaignId={campaignId}
          onCreated={handleCreated}
          onCancel={() => setModalOpen(false)}
        />
      </Modal>
    </div>
  );
}
